import { useEffect, useRef, useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ApiError,
  enrollRecoveryCodes,
  fetchEmailCodeStatus,
  registerPasskey,
  requestEmailCode,
  supportsPasskeys,
  verifyEmailCode,
} from '../lib/api'
import { WebAuthnUserError } from '../lib/webauthnCeremony'
import { useUiStore } from '../lib/uiStore'

type Step = 'hidden' | 'idle' | 'email' | 'code' | 'recovery' | 'passkey_offer' | 'done'

type Props = {
  /** login → /inbox ; signup → recovery then /inbox?sec=add */
  mode: 'login' | 'signup'
}

/**
 * Secondary email-OTP auth — shown only when EMAIL_CODE_LOGIN_ENABLED.
 * Passkey remains the primary CTA on the parent page.
 */
export function EmailCodePanel({ mode }: Props) {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const locale = useUiStore((s) => s.locale)
  const [step, setStep] = useState<Step>('hidden')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [codes, setCodes] = useState<string[] | null>(null)
  const [isNew, setIsNew] = useState(false)
  const inFlight = useRef(false)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const st = await fetchEmailCodeStatus()
        if (!cancelled) setStep(st.email_code_login_enabled ? 'idle' : 'hidden')
      } catch {
        if (!cancelled) setStep('hidden')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  async function onRequest(e: FormEvent) {
    e.preventDefault()
    if (inFlight.current || !email.trim()) return
    inFlight.current = true
    setError(null)
    setBusy(true)
    try {
      await requestEmailCode(email.trim(), locale || i18n.language || 'en')
      setStep('code')
    } catch (err) {
      if (err instanceof ApiError) {
        const d = String(err.message || '')
        if (err.status === 403 || /email_code_closed/i.test(d)) setStep('hidden')
        else if (err.status === 429 || /rate limit/i.test(d)) setError(t('auth.emailCodeRateLimited'))
        else if (/smtp/i.test(d)) setError(t('auth.emailCodeSmtpUnavailable'))
        else setError(d || t('auth.emailCodeRequestFailed'))
      } else {
        setError(t('auth.emailCodeRequestFailed'))
      }
    } finally {
      setBusy(false)
      inFlight.current = false
    }
  }

  async function onVerify(e: FormEvent) {
    e.preventDefault()
    if (inFlight.current || !code.trim()) return
    inFlight.current = true
    setError(null)
    setBusy(true)
    try {
      const bundle = await verifyEmailCode(email.trim(), code.trim())
      const neu = Boolean(bundle.is_new)
      setIsNew(neu)
      if (neu || mode === 'signup') {
        setStep('recovery')
        try {
          const res = await enrollRecoveryCodes()
          setCodes(res.codes)
        } catch {
          /* optional — may already exist for returning inactive */
        }
      } else {
        navigate('/inbox', { replace: true })
      }
    } catch (err) {
      if (err instanceof ApiError) {
        const d = String(err.message || '')
        if (err.status === 429 || /locked|rate limit/i.test(d)) setError(t('auth.emailCodeLocked'))
        else setError(t('auth.emailCodeInvalid'))
      } else {
        setError(t('auth.emailCodeInvalid'))
      }
    } finally {
      setBusy(false)
      inFlight.current = false
    }
  }

  function afterRecovery(offerPasskey: boolean) {
    if (offerPasskey && isNew) {
      setStep('passkey_offer')
      return
    }
    navigate(isNew || mode === 'signup' ? '/inbox?sec=add' : '/inbox', { replace: true })
  }

  async function onEnrollPasskey() {
    if (!supportsPasskeys()) {
      afterRecovery(false)
      return
    }
    setBusy(true)
    setError(null)
    try {
      await registerPasskey('Primary')
      navigate('/inbox?sec=add', { replace: true })
    } catch (err) {
      if (err instanceof WebAuthnUserError && err.code === 'cancelled') {
        setError(t('auth.passkeyCancelled'))
      } else {
        setError(err instanceof ApiError ? err.message : t('auth.registerFailed'))
      }
    } finally {
      setBusy(false)
    }
  }

  if (step === 'hidden') return null

  if (step === 'idle') {
    return (
      <div className="email-code-panel">
        <button type="button" className="btn btn-ghost" onClick={() => setStep('email')}>
          {t('auth.emailCodeCta')}
        </button>
        <p className="muted small">{t('auth.emailCodeHint')}</p>
      </div>
    )
  }

  if (step === 'email') {
    return (
      <div className="email-code-panel auth-form">
        <p className="lede">{t('auth.emailCodeLede')}</p>
        <form onSubmit={(e) => void onRequest(e)}>
          <label className="field">
            <span>{t('auth.email')}</span>
            <input
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(ev) => setEmail(ev.target.value)}
            />
          </label>
          {error ? (
            <p className="warn" role="alert">
              {error}
            </p>
          ) : null}
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? t('auth.working') : t('auth.emailCodeSend')}
          </button>
          <button type="button" className="btn btn-ghost" onClick={() => setStep('idle')}>
            {t('auth.cancel')}
          </button>
        </form>
      </div>
    )
  }

  if (step === 'code') {
    return (
      <div className="email-code-panel auth-form">
        <p className="ok">{t('auth.emailCodeSent')}</p>
        <form onSubmit={(e) => void onVerify(e)}>
          <label className="field">
            <span>{t('auth.emailCodeLabel')}</span>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{6}"
              maxLength={12}
              value={code}
              onChange={(ev) => setCode(ev.target.value)}
              required
            />
          </label>
          {error ? (
            <p className="warn" role="alert">
              {error}
            </p>
          ) : null}
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? t('auth.working') : t('auth.emailCodeVerify')}
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            disabled={busy}
            onClick={() => void onRequest({ preventDefault() {} } as FormEvent)}
          >
            {t('auth.emailCodeResend')}
          </button>
        </form>
      </div>
    )
  }

  if (step === 'recovery') {
    return (
      <div className="email-code-panel auth-form">
        <h2>{t('auth.signupRecoveryTitle')}</h2>
        <p className="lede">{t('auth.signupRecoveryLede')}</p>
        {codes ? (
          <div className="codes-box">
            <p className="warn">{t('auth.codesOnce')}</p>
            <ul className="codes-list">
              {codes.map((c) => (
                <li key={c}>
                  <code>{c}</code>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <p className="muted">{t('auth.codesFailed')}</p>
        )}
        <button type="button" className="btn btn-primary" onClick={() => afterRecovery(true)}>
          {t('auth.emailCodeContinue')}
        </button>
        <button type="button" className="btn btn-ghost" onClick={() => afterRecovery(false)}>
          {t('auth.signupSkipCodes')}
        </button>
      </div>
    )
  }

  if (step === 'passkey_offer') {
    return (
      <div className="email-code-panel auth-form">
        <h2>{t('auth.emailCodePasskeyOfferTitle')}</h2>
        <p className="lede">{t('auth.emailCodePasskeyOfferLede')}</p>
        {error ? (
          <p className="warn" role="alert">
            {error}
          </p>
        ) : null}
        <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void onEnrollPasskey()}>
          {busy ? t('auth.working') : t('auth.registerPasskey')}
        </button>
        <button type="button" className="btn btn-ghost" onClick={() => afterRecovery(false)}>
          {t('auth.emailCodeSkipPasskey')}
        </button>
      </div>
    )
  }

  return null
}
